import styled from '@emotion/styled'
import { useCallback, useEffect, useRef, useState } from 'react'
import {
    BUTTON_BORDER_RADIUS_VAR,
    PRIMARY_PAGE_TEXT_COLOR_VAR,
    SUBTLE_PRIMARY_COLOR_VAR,
} from '../../../context/CustomizationContextProvider'
import { hoverTintSurface } from '../../../style/interactiveStates'
import { scaledFontSize } from '../../../style/scrollingLayout'
import { getTypographyRoleStyle } from '../../../style/typography'
import { ButtonLoadingSpinner, TrashIcon } from '../../icons/Icons'

const QUANTITY_SELECTOR_HEIGHT = 30
const QUANTITY_SELECTOR_TOP_MARGIN = 7

const QuantityContainer = styled.div<{ disabled: boolean; isCart?: boolean }>(
    ({ disabled, isCart }) => ({
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 4,
        border: `${isCart ? '0.75px' : '1px'} solid var(${PRIMARY_PAGE_TEXT_COLOR_VAR})`,
        borderRadius: `var(${BUTTON_BORDER_RADIUS_VAR})`,
        opacity: disabled ? 0.5 : 1,
        height: isCart ? QUANTITY_SELECTOR_HEIGHT : 34,
        boxSizing: 'border-box',
        ...(isCart && {
            marginTop: QUANTITY_SELECTOR_TOP_MARGIN,
            width: '100px',
        }),
    }),
)

const QuantityButton = styled.button({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 30,
    height: 30,
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: 0,
    margin: 0,
    color: `var(${PRIMARY_PAGE_TEXT_COLOR_VAR})`,
    ...getTypographyRoleStyle('labelText'),
    fontSize: scaledFontSize(14),
    borderRadius: '100%',
    ...hoverTintSurface(),
    '&:disabled': {
        opacity: 0.5,
        cursor: 'not-allowed',
    },
})

const QuantityText = styled.input({
    width: 24,
    height: 24,
    textAlign: 'center',
    border: 'none',
    background: 'none',
    color: `var(${PRIMARY_PAGE_TEXT_COLOR_VAR})`,
    ...getTypographyRoleStyle('labelText', {
        fontWeight: 400,
    }),
    fontSize: scaledFontSize(14),
    appearance: 'textfield',
    '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
        WebkitAppearance: 'none',
        margin: 0,
    },
})

export const QuantityStepper = ({
    quantity,
    setQuantity,
    disabled,
    loading,
    onDelete,
    isCart = false,
}: {
    quantity: number
    setQuantity: (quantity: number) => void
    disabled: boolean
    loading: boolean
    onDelete?: () => void
    isCart?: boolean
}) => {
    const [localQuantity, setLocalQuantity] = useState<number>(quantity)
    // Resync the local draft whenever the committed quantity changes upstream
    // (e.g. another tab, or a server-driven adjustment), overriding any
    // in-progress edit.
    const [prevQuantity, setPrevQuantity] = useState(quantity)
    if (quantity !== prevQuantity) {
        setPrevQuantity(quantity)
        setLocalQuantity(quantity)
    }
    const debounceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
        null,
    )

    const debouncedSetQuantity = useCallback(
        (value: number) => {
            if (debounceTimeoutRef.current) {
                clearTimeout(debounceTimeoutRef.current)
                debounceTimeoutRef.current = null
            }
            debounceTimeoutRef.current = setTimeout(() => {
                if (value === 0 && onDelete) {
                    onDelete()
                } else {
                    setQuantity(value)
                    setLocalQuantity(value)
                }
                debounceTimeoutRef.current = null
            }, 500)
        },
        [setQuantity, onDelete, setLocalQuantity],
    )

    useEffect(() => {
        return () => {
            if (debounceTimeoutRef.current) {
                clearTimeout(debounceTimeoutRef.current)
            }
        }
    }, [])

    function sanitizedQuantity(value: number) {
        if (isNaN(value) || value < 1) {
            return 1
        }
        return value
    }

    return (
        <QuantityContainer disabled={disabled} isCart={isCart}>
            <QuantityButton
                onClick={() => {
                    const newQuantity =
                        localQuantity === 1 && onDelete
                            ? 0
                            : sanitizedQuantity(localQuantity - 1)
                    setLocalQuantity(newQuantity)
                    debouncedSetQuantity(newQuantity)
                }}
                disabled={disabled}
                aria-label={
                    localQuantity === 1 ? 'Remove item' : 'Decrease quantity'
                }
            >
                {localQuantity === 1 ? (
                    <TrashIcon
                        width={16}
                        color={`var(${PRIMARY_PAGE_TEXT_COLOR_VAR})`}
                        disabledColor={`var(${SUBTLE_PRIMARY_COLOR_VAR})`}
                        disabled={disabled}
                    />
                ) : (
                    '-'
                )}
            </QuantityButton>
            {loading && (
                <ButtonLoadingSpinner
                    color={`var(${PRIMARY_PAGE_TEXT_COLOR_VAR})`}
                />
            )}
            {!loading && (
                <QuantityText
                    type="number"
                    min={1}
                    value={localQuantity}
                    // We allow NaN values here to allow the user to be able to
                    // delete the last digit in order to replace it.
                    onChange={(e) =>
                        setLocalQuantity(parseInt(e.target.value, 10))
                    }
                    onBlur={(e) => {
                        const value = parseInt(e.target.value, 10)
                        const finalQuantity =
                            value === 0 && onDelete
                                ? 0
                                : sanitizedQuantity(value)
                        setLocalQuantity(finalQuantity)
                        debouncedSetQuantity(finalQuantity)
                    }}
                    disabled={disabled}
                    aria-label="Quantity"
                />
            )}
            <QuantityButton
                onClick={() => {
                    const newQuantity = sanitizedQuantity(localQuantity + 1)
                    setLocalQuantity(newQuantity)
                    debouncedSetQuantity(newQuantity)
                }}
                disabled={disabled}
                aria-label="Increase quantity"
            >
                +
            </QuantityButton>
        </QuantityContainer>
    )
}
